// ATE-CR-117
import { useEffect, useState } from "react";

import {
  listEscalations,
  resolveEscalation,
  type EscalationListItem,
  type EscalationResolution,
} from "@/api/escalations.api";
import { useToast } from "@/components/common/useToast";
import { useMe } from "@/hooks/useMe";

import { EscalationActionModal } from "./EscalationActionModal";
import { EscalationDetailDrawer } from "./EscalationDetailDrawer";
import "./EscalationsListPage.css";

interface RowState {
  readonly resolving: boolean;
}

// ATE-CR-117
export function EscalationsListPage(): JSX.Element {
  const { me, loading: meLoading } = useMe();
  const { toast } = useToast();

  const [escalations, setEscalations] = useState<EscalationListItem[]>([]);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<Error | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<EscalationResolution>("commit");
  const [modalEscalation, setModalEscalation] = useState<EscalationListItem | null>(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEscalation, setDrawerEscalation] = useState<EscalationListItem | null>(null);

  const isSuperAdmin = me?.system_role === "SUPER_ADMIN";

  // ATE-CR-117: fetch escalations with AbortController + cancellation guard
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    const ctrl = new AbortController();
    setListLoading(true);
    setListError(null);

    listEscalations(ctrl.signal)
      .then((rows) => {
        if (cancelled) return;
        setEscalations(rows);
        setListLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setListError(err);
        setListLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [isSuperAdmin]);

  // While useMe is loading, show nothing (avoid flash of 403)
  if (meLoading) {
    return <div aria-busy="true">Loading…</div>;
  }

  // Role guard: only SUPER_ADMIN may access this page
  if (!isSuperAdmin) {
    return (
      <div role="alert" className="escalations-page__forbidden">
        Forbidden — super_admin only
      </div>
    );
  }

  // ATE-CR-117: open commit/release modal for a row
  function openModal(action: EscalationResolution, esc: EscalationListItem): void {
    setModalAction(action);
    setModalEscalation(esc);
    setModalOpen(true);
  }

  function closeModal(): void {
    setModalOpen(false);
    setModalEscalation(null);
  }

  // ATE-CR-117: open detail drawer for a row
  function openDrawer(esc: EscalationListItem): void {
    setDrawerEscalation(esc);
    setDrawerOpen(true);
  }

  function closeDrawer(): void {
    setDrawerOpen(false);
    setDrawerEscalation(null);
  }

  // ATE-CR-117: optimistic resolve — mark row resolving → call API → drop or revert
  async function handleConfirm(note: string): Promise<void> {
    if (!modalEscalation) return;
    const esc = modalEscalation;
    const action = modalAction;
    const shortId = esc.request_id.slice(0, 8);

    // Close modal immediately (optimistic UX)
    closeModal();

    // Mark row as resolving
    setRowState((prev) => ({
      ...prev,
      [esc.request_id]: { resolving: true },
    }));

    try {
      await resolveEscalation(esc.request_id, { resolution: action, note: note || undefined });
      // Success: drop row from list
      setEscalations((prev) => prev.filter((r) => r.request_id !== esc.request_id));
      setRowState((prev) => {
        const next = { ...prev };
        delete next[esc.request_id];
        return next;
      });
      const verb = action === "commit" ? "Committed" : "Released";
      toast("success", `${verb} escalation ${shortId}`);
    } catch (err) {
      // Error: revert row state
      setRowState((prev) => {
        const next = { ...prev };
        delete next[esc.request_id];
        return next;
      });
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      toast("error", `Failed to ${action}: ${errMsg}`);
    }
  }

  return (
    <div className="escalations-page">
      <header className="escalations-page__header">
        <h1 className="escalations-page__title">Escalations</h1>
        <span className="escalations-page__badge">
          Pending: {escalations.length}
        </span>
      </header>

      {listLoading && (
        <div aria-busy="true" className="escalations-page__loading">
          Loading escalations…
        </div>
      )}

      {listError && (
        <div role="alert" className="escalations-page__error">
          Failed to load escalations: {listError.message}
        </div>
      )}

      {!listLoading && !listError && (
        <table className="escalations-table">
          <thead>
            <tr>
              <th scope="col">Escalated</th>
              <th scope="col">Tenant ID</th>
              <th scope="col">Resource</th>
              <th scope="col">Request ID</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {escalations.map((esc) => {
              const isResolving = rowState[esc.request_id]?.resolving ?? false;
              const shortId = esc.request_id.slice(0, 8);
              const formattedDate = new Date(esc.escalated_at).toLocaleString();
              const shortTenantId = esc.tenant_id.slice(0, 8);

              return (
                <tr
                  key={esc.request_id}
                  className={
                    isResolving
                      ? "escalations-table__row escalations-table__row--resolving"
                      : "escalations-table__row"
                  }
                  aria-busy={isResolving || undefined}
                >
                  <td>{formattedDate}</td>
                  <td className="escalations-page__cell escalations-page__tenant-cell">
                    <button
                      type="button"
                      className="escalations-page__row-trigger"
                      onClick={() => !isResolving && openDrawer(esc)}
                      disabled={isResolving}
                      title={esc.tenant_id}
                    >
                      {shortTenantId}…
                    </button>
                  </td>
                  <td>{esc.held_resource}</td>
                  <td>
                    <code title={esc.request_id}>{shortId}…</code>
                  </td>
                  <td>
                    <span className="badge badge--escalated">{esc.status}</span>
                  </td>
                  <td className="escalations-table__actions">
                    {isResolving ? (
                      <span className="escalations-table__resolving-label">
                        Resolving…
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn--sm btn--primary"
                          onClick={() => openModal("commit", esc)}
                        >
                          Commit
                        </button>
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          onClick={() => openModal("release", esc)}
                        >
                          Release
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modalEscalation && (
        <EscalationActionModal
          open={modalOpen}
          action={modalAction}
          escalation={modalEscalation}
          onConfirm={(note) => void handleConfirm(note)}
          onCancel={closeModal}
        />
      )}

      {drawerEscalation && (
        <EscalationDetailDrawer
          open={drawerOpen}
          escalation={drawerEscalation}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}
